import React, { useState } from 'react';

interface PreviewRow {
  nip: string;
  employeeName: string;
  date: string;
  scheduleType: string;
  isUpdate: boolean;
  oldValue?: string;
}

interface ImportPreviewTableProps {
  data: PreviewRow[];
  totalRows: number;
  validRows: number;
  errorRows: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  data,
  totalRows,
  validRows,
  errorRows,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 100;
  const totalPages = Math.ceil(data.length / rowsPerPage);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  return (
    <div className="w-full">
      {/* Summary */}
      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="p-3 bg-blue-50 rounded">
          <p className="text-xs text-gray-600">Total Baris</p>
          <p className="text-lg font-bold text-blue-600">{totalRows}</p>
        </div>
        <div className="p-3 bg-green-50 rounded">
          <p className="text-xs text-gray-600">Baris Valid</p>
          <p className="text-lg font-bold text-green-600">{validRows}</p>
        </div>
        <div className="p-3 bg-red-50 rounded">
          <p className="text-xs text-gray-600">Baris Error</p>
          <p className="text-lg font-bold text-red-600">{errorRows}</p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto overflow-y-auto rounded max-h-96">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">NIP</th>
              <th className="px-4 py-2 text-left font-semibold">Nama Karyawan</th>
              <th className="px-4 py-2 text-left font-semibold">Tanggal</th>
              <th className="px-4 py-2 text-left font-semibold">Schedule Type</th>
              <th className="px-4 py-2 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, index) => (
              <tr
                key={index}
                className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td className="px-4 py-2">{row.nip}</td>
                <td className="px-4 py-2">{row.employeeName}</td>
                <td className="px-4 py-2">{row.date}</td>
                <td className="px-4 py-2">{row.scheduleType}</td>
                <td className="px-4 py-2">
                  {row.isUpdate ? (
                    <span
                      className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium"
                      title={`Lama: ${row.oldValue}`}
                    >
                      Update
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                      Baru
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Halaman {currentPage} dari {totalPages} | Menampilkan {startIndex + 1}-{Math.min(endIndex, data.length)} dari {data.length} baris
        </div>
        
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            ←
          </button>

          <div className="flex gap-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let page;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded ${
                    currentPage === page
                      ? 'bg-blue-500 text-white'
                      : 'border hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            →
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex gap-3 justify-end">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="px-6 py-2 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Batal
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Importing...' : 'Import'}
        </button>
      </div>
    </div>
  );
};

export default ImportPreviewTable;
